import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QuestCard } from '@/components/quests/QuestCard';
import { Button } from '@/components/ui/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { useTelegramBackButton } from '@/hooks/useTelegramBackButton';
import {
  getAllQuests,
  startQuest,
  claimQuestReward,
  getAchievements,
  type Quest,
  type Achievement,
  type ClaimRewardResult,
} from '@/services/quests';

interface QuestsPageProps {
  onClose: () => void;
}

type Tab = 'daily' | 'weekly' | 'story' | 'achievements';

export function QuestsPage({ onClose }: QuestsPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('daily');
  const [quests, setQuests] = useState<{
    daily: Quest[];
    weekly: Quest[];
    story: Quest[];
    special: Quest[];
  }>({ daily: [], weekly: [], story: [], special: [] });
  const [achievements, setAchievements] = useState<{
    unlocked: Achievement[];
    locked: Achievement[];
  }>({ unlocked: [], locked: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [loadingQuestId, setLoadingQuestId] = useState<string | null>(null);
  const [rewardResult, setRewardResult] = useState<ClaimRewardResult | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  const { hapticFeedback } = useTelegram();

  // Telegram BackButton
  useTelegramBackButton(onClose);

  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setIsLoading(true);
      const [questsData, achievementsData] = await Promise.all([
        getAllQuests(),
        getAchievements(),
      ]);
      setQuests(questsData);
      setAchievements(achievementsData);
    } catch (error) {
      console.error('Failed to load quests:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleStartQuest = async (questId: string) => {
    try {
      setLoadingQuestId(questId);
      await startQuest(questId);
      hapticFeedback('light');
      await loadData();
    } catch (error) {
      console.error('Failed to start quest:', error);
      hapticFeedback('error');
    } finally {
      setLoadingQuestId(null);
    }
  };
  
  const handleClaimReward = async (userQuestId: string) => {
    try {
      setLoadingQuestId(userQuestId);
      const result = await claimQuestReward(userQuestId);
      setRewardResult(result);
      hapticFeedback('success');
      await loadData();
    } catch (error) {
      console.error('Failed to claim reward:', error);
      hapticFeedback('error');
    } finally {
      setLoadingQuestId(null);
    }
  };
  
  const tabs: { id: Tab; label: string; emoji: string }[] = [
    { id: 'daily', label: 'День', emoji: '☀️' },
    { id: 'weekly', label: 'Неделя', emoji: '🌙' },
    { id: 'story', label: 'Сюжет', emoji: '📖' },
    { id: 'achievements', label: 'Награды', emoji: '🏆' },
  ];
  
  const currentQuests = activeTab === 'achievements' 
    ? [] 
    : quests[activeTab] || [];
  
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex flex-col items-center p-4 pb-2 border-b border-primary-800/50 relative">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-white">Квесты</h1>
          <button
            onClick={() => setShowInfoModal(true)}
            className="w-6 h-6 rounded-full bg-primary-700/50 flex items-center justify-center text-primary-400 text-xs"
          >
            ?
          </button>
        </div>
        <p className="text-xs text-primary-500">Онлайн-задания для развития персонажа</p>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-primary-800/50">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-800/50 text-white'
                : 'text-primary-500'
            }`}
          >
            <span className="text-lg">{tab.emoji}</span>
            <span className="text-xs">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : activeTab === 'achievements' ? (
          <AchievementsView achievements={achievements} />
        ) : (
          <QuestsView
            quests={currentQuests}
            loadingQuestId={loadingQuestId}
            onStart={handleStartQuest}
            onClaim={handleClaimReward}
          />
        )}
      </div>
      
      {/* Reward Modal */}
      <AnimatePresence>
        {rewardResult && (
          <RewardModal
            result={rewardResult}
            onClose={() => setRewardResult(null)}
          />
        )}
      </AnimatePresence>

      {/* Info Modal */}
      <AnimatePresence>
        {showInfoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
            onClick={() => setShowInfoModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-primary-900 rounded-2xl p-6 w-full max-w-sm border border-primary-700/50"
            >
              <h2 className="text-lg font-bold text-white mb-4 text-center">Что такое квесты?</h2>

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <span className="text-lg">📅</span>
                  <div>
                    <p className="font-medium text-white">Дневные</p>
                    <p className="text-primary-400 text-xs">Маленькие задания на сегодня (5-15 мин)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">📆</span>
                  <div>
                    <p className="font-medium text-white">Недельные</p>
                    <p className="text-primary-400 text-xs">Челленджи на всю неделю</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">📖</span>
                  <div>
                    <p className="font-medium text-white">Сюжетные</p>
                    <p className="text-primary-400 text-xs">Твоя личная история развития</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">🏆</span>
                  <div>
                    <p className="font-medium text-white">Награды</p>
                    <p className="text-primary-400 text-xs">Достижения за прогресс</p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-primary-400 text-center mt-4 pt-3 border-t border-primary-700/50">
                Выполняй квесты → получай опыт → прокачивай характеристики
              </p>

              <Button onClick={() => setShowInfoModal(false)} fullWidth className="mt-4">
                Понятно
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface QuestsViewProps {
  quests: Quest[];
  loadingQuestId: string | null;
  onStart: (questId: string) => void;
  onClaim: (userQuestId: string) => void;
}

function QuestsView({ quests, loadingQuestId, onStart, onClaim }: QuestsViewProps) {
  if (quests.length === 0) {
    return (
      <div className="text-center py-12">
        <span className="text-4xl mb-4 block">📋</span>
        <p className="text-primary-400">Квестов пока нет</p>
        <p className="text-primary-500 text-sm mt-1">Загляни позже!</p>
      </div>
    );
  }
  
  // Сортируем: сначала незавершённые, потом завершённые, потом полученные
  const sortedQuests = [...quests].sort((a, b) => {
    const statusOrder: Record<string, number> = {
      completed: 0,
      in_progress: 1,
      not_started: 2,
      claimed: 3,
      expired: 4,
    };
    
    const aStatus = a.userProgress?.status || 'not_started';
    const bStatus = b.userProgress?.status || 'not_started';
    
    return (statusOrder[aStatus] || 5) - (statusOrder[bStatus] || 5);
  });
  
  return (
    <div className="space-y-3">
      {sortedQuests.map(quest => (
        <QuestCard
          key={quest.id}
          quest={quest}
          isLoading={loadingQuestId === quest.id}
          onStart={() => onStart(quest.id)}
          onClaim={() => {
            // Нужен ID userQuest для claim, пока используем quest.id
            // В реальности нужно хранить userQuestId
            onClaim(quest.id);
          }}
        />
      ))}
    </div>
  );
}

interface AchievementsViewProps {
  achievements: {
    unlocked: Achievement[];
    locked: Achievement[];
  };
}

function AchievementsView({ achievements }: AchievementsViewProps) {
  return (
    <div className="space-y-6">
      {/* Unlocked */}
      {achievements.unlocked.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-primary-400 mb-3">
            Полученные ({achievements.unlocked.length})
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {achievements.unlocked.map(achievement => (
              <motion.div
                key={achievement.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-gradient-to-br from-amber-900/30 to-amber-800/20 border border-amber-700/30 rounded-xl p-4 text-center"
              >
                <span className="text-3xl block mb-2">{achievement.emoji}</span>
                <p className="font-medium text-white text-sm">{achievement.name}</p>
                <p className="text-xs text-primary-400 mt-1">{achievement.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      
      {/* Locked */}
      {achievements.locked.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-primary-400 mb-3">
            Заблокированные ({achievements.locked.length})
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {achievements.locked.map(achievement => (
              <div
                key={achievement.id}
                className="bg-primary-900/30 border border-primary-800/30 rounded-xl p-4 text-center opacity-60"
              >
                <span className="text-3xl block mb-2 grayscale">🔒</span>
                <p className="font-medium text-primary-400 text-sm">{achievement.name}</p>
                <p className="text-xs text-primary-500 mt-1">{achievement.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {achievements.unlocked.length === 0 && achievements.locked.length === 0 && (
        <div className="text-center py-12">
          <span className="text-4xl mb-4 block">🏆</span>
          <p className="text-primary-400">Достижений пока нет</p>
          <p className="text-primary-500 text-sm mt-1">Выполняй квесты чтобы получить награды!</p>
        </div>
      )}
    </div>
  );
}

interface RewardModalProps {
  result: ClaimRewardResult;
  onClose: () => void;
}

function RewardModal({ result, onClose }: RewardModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-2xl p-6 w-full max-w-sm border border-primary-700/50"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 }}
            className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center mb-4"
          >
            <span className="text-4xl">🎁</span>
          </motion.div>
          
          <h2 className="text-xl font-bold text-white mb-2">Награда получена!</h2>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-primary-800/50 rounded-xl p-4 mb-4"
          >
            <p className="text-3xl font-bold text-primary-300">+{result.xpGained} XP</p>
          </motion.div>
          
          {result.levelUp && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-r from-creator/20 to-creator/10 border border-creator/30 rounded-xl p-4 mb-4"
            >
              <p className="text-creator font-bold">🎉 Новый уровень {result.newLevel}!</p>
            </motion.div>
          )}
          
          {result.achievementUnlocked && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-amber-900/30 to-amber-800/20 border border-amber-700/30 rounded-xl p-4 mb-4"
            >
              <p className="text-amber-400 font-bold mb-1">🏆 Новое достижение!</p>
              <p className="text-white">{result.achievementUnlocked.name}</p>
              <p className="text-primary-400 text-sm">{result.achievementUnlocked.description}</p>
            </motion.div>
          )}
          
          <Button onClick={onClose} fullWidth>
            Отлично!
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
